import React, { useState } from 'react';
import { Send, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { mockData } from '../mock';
import { toast } from 'sonner';

const FORMSUBMIT_EMAIL = process.env.REACT_APP_FORMSUBMIT_EMAIL || 'hello@grabo.ai';
const FORMSUBMIT_URL = `https://formsubmit.co/${FORMSUBMIT_EMAIL}`;

export const ContactForm = () => {
  const { finalCTA } = mockData;
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    organization: '',
    location: '',
    spaceType: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Prepare form data for FormSubmit
      const submitData = {
        ...formData,
        _subject: `New Contact Form Submission - ${formData.organization || 'Unknown Organization'}`,
        _template: 'table',
        _captcha: 'false', // Disable captcha (optional, can be removed if you want captcha)
      };

      const response = await fetch(FORMSUBMIT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(submitData),
      });

      // FormSubmit returns success for 200 status codes
      if (response.ok) {
        let data;
        try {
          data = await response.json();
        } catch {
          // If response is not JSON, that's okay - FormSubmit still processed it
          data = { success: true };
        }
        
        toast.success('Thank you! We\'ll be in touch soon.');
        // Reset form
        setFormData({
          name: '',
          phone: '',
          email: '',
          organization: '',
          location: '',
          spaceType: '',
          message: '',
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to submit form');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, spaceType: value }));
  };

  return (
    <section id="contact" className="py-20 lg:py-32 bg-white">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 md:mb-12">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 md:mb-4 tracking-tight">
            {finalCTA.title}
          </h2>
          <p className="text-base md:text-lg text-gray-600">{finalCTA.subtitle}</p>
        </div>

        {/* Contact Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-gray-700 font-medium">Name *</Label>
              <Input
                id="name"
                name="name"
                type="text"
                required
                value={formData.name}
                onChange={handleChange}
                placeholder="Your name"
                className="border-gray-300 focus:border-[#2AD7C5] focus:ring-[#2AD7C5]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-gray-700 font-medium">Phone Number *</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                required
                value={formData.phone}
                onChange={handleChange}
                placeholder="+971 XXX XXX XXX"
                className="border-gray-300 focus:border-[#2AD7C5] focus:ring-[#2AD7C5]"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-700 font-medium">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your@email.com"
                className="border-gray-300 focus:border-[#2AD7C5] focus:ring-[#2AD7C5]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="organization" className="text-gray-700 font-medium">Organization *</Label>
              <Input
                id="organization"
                name="organization"
                type="text"
                required
                value={formData.organization}
                onChange={handleChange}
                placeholder="Company or institution"
                className="border-gray-300 focus:border-[#2AD7C5] focus:ring-[#2AD7C5]"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="location" className="text-gray-700 font-medium">Location *</Label>
            <Input
              id="location"
              name="location"
              type="text"
              required
              value={formData.location}
              onChange={handleChange}
              placeholder="City, Country"
              className="border-gray-300 focus:border-[#2AD7C5] focus:ring-[#2AD7C5]"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="spaceType" className="text-gray-700 font-medium">Type of Space *</Label>
            <Select onValueChange={handleSelectChange} value={formData.spaceType} required>
              <SelectTrigger className="border-gray-300 focus:border-[#2AD7C5] focus:ring-[#2AD7C5]">
                <SelectValue placeholder="Select your space type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="office">Office / Corporate Campus</SelectItem>
                <SelectItem value="university">University / School</SelectItem>
                <SelectItem value="hospital">Hospital / Healthcare</SelectItem>
                <SelectItem value="gym">Gym / Fitness Center</SelectItem>
                <SelectItem value="residential">Residential Tower</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message" className="text-gray-700 font-medium">Message</Label>
            <Textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Tell us about your space and what you're looking for..."
              rows={5}
              className="border-gray-300 focus:border-[#2AD7C5] focus:ring-[#2AD7C5] resize-none"
            />
          </div>

          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-[#2AD7C5] hover:bg-[#24c0b3] text-white rounded-full py-6 text-lg font-medium transition-all duration-200 hover:scale-[1.02] shadow-lg shadow-[#2AD7C5]/20 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Submitting...' : finalCTA.buttonText}
            <Send className="ml-2" size={20} />
          </Button>
        </form>

        {/* Alternative Contact */}
        <div className="mt-8 text-center">
          <p className="text-gray-600">
            {finalCTA.alternativeText.split('hello@grabo.ai')[0]}
            <a
              href="mailto:hello@grabo.ai"
              className="inline-flex items-center gap-1 text-[#2AD7C5] hover:text-[#24c0b3] font-medium transition-colors"
            >
              hello@grabo.ai
            </a>
          </p>
        </div>
      </div>
    </section>
  );
};
